import { useEffect, useMemo, useState } from 'react';
import { Flame, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const DAY = 86_400_000;
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function dayKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * "Your Week" — compact engagement strip showing the rep's last 7 days of
 * activity + current streak. Only renders for real-mode reps.
 */
export function MyWeekCard() {
  const { mode, userId, dealershipId } = useAuth();
  const [activeDays, setActiveDays] = useState<Set<string>>(new Set());
  const [activityCount, setActivityCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mode !== 'real' || !userId || !dealershipId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const since = new Date(Date.now() - 7 * DAY).toISOString();
      // Activities are the strongest "did real work" signal.
      const { data } = await supabase
        .from('activities')
        .select('created_at')
        .eq('rep_id', userId)
        .gte('created_at', since);
      if (!active) return;
      const days = new Set<string>();
      (data ?? []).forEach(a => days.add(dayKey(new Date(a.created_at))));
      setActiveDays(days);
      setActivityCount(data?.length ?? 0);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [mode, userId, dealershipId]);

  const week = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today.getTime() - (6 - i) * DAY);
      return {
        date: d,
        active: activeDays.has(dayKey(d)),
        label: DAY_LABELS[d.getDay()],
        isToday: i === 6,
      };
    });
  }, [activeDays]);

  // Streak = consecutive active days ending today (or yesterday if today not yet active).
  const streak = useMemo(() => {
    let s = 0;
    for (let i = week.length - 1; i >= 0; i--) {
      if (week[i].active) s += 1;
      else if (i === week.length - 1) continue; // grace for today
      else break;
    }
    return s;
  }, [week]);

  if (mode !== 'real') return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" /> Your Week
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Streak</p>
            <p className="text-3xl font-bold flex items-center gap-1">
              {streak}
              {streak >= 3 && <Flame className="h-6 w-6 text-orange-400" />}
            </p>
            <p className="text-xs text-muted-foreground">{streak === 1 ? 'day' : 'days'} in a row</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Activities</p>
            <p className="text-3xl font-bold">{activityCount}</p>
            <p className="text-xs text-muted-foreground">last 7 days</p>
          </div>
        </div>
        <div className="flex items-end justify-between gap-1">
          {week.map((d, i) => (
            <div key={i} className="flex flex-col items-center gap-1 flex-1">
              <div
                className={`h-9 w-full rounded-md transition-colors ${
                  d.active
                    ? 'bg-primary/80'
                    : d.isToday
                    ? 'bg-muted border border-dashed border-primary/40'
                    : 'bg-muted/40'
                }`}
                title={d.date.toLocaleDateString()}
              />
              <span className={`text-[10px] ${d.isToday ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                {d.label}
              </span>
            </div>
          ))}
        </div>
        {!loading && activityCount === 0 && (
          <p className="text-xs text-muted-foreground text-center pt-1">
            Log one activity to start your streak.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
